import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatPrice } from '../data/products';
import { useCart } from '../lib/cart';

export function CartDrawer() {
  const { items, count, subtotalCents, currency, isOpen, closeCart, removeItem, setQuantity } = useCart();

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, closeCart]);

  if (!isOpen) return null;

  return (
    <div className="cart-overlay" role="presentation" onClick={closeCart}>
      <aside
        className="card cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="cart-drawer-top">
          <div>
            <span className="eyebrow" style={{ marginBottom: '0.35rem' }}>
              Your cart
            </span>
            <h2>{count ? `${count} item${count === 1 ? '' : 's'}` : 'Cart is empty'}</h2>
          </div>
          <button type="button" className="cart-close" onClick={closeCart} aria-label="Close cart">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="cart-drawer-body">
          {items.length === 0 ? (
            <p className="muted">Add an EspoCRM extension from the marketplace to get started.</p>
          ) : (
            <ul className="cart-lines">
              {items.map((item) => (
                <li key={item.id} className="cart-line">
                  <div className="cart-line-main">
                    <div className="cart-line-icon">
                      {item.heroImageUrl ? (
                        <img src={item.heroImageUrl} alt="" />
                      ) : (
                        <span className="material-symbols-outlined">{item.icon || 'extension'}</span>
                      )}
                    </div>
                    <div className="cart-line-copy">
                      <strong>{item.name}</strong>
                      <span className="muted">
                        {item.version ? `v${item.version} · ` : ''}
                        {formatPrice(item.priceCents, item.currency)} each
                      </span>
                    </div>
                  </div>
                  <div className="cart-line-actions">
                    <div className="cart-qty">
                      <button type="button" onClick={() => setQuantity(item.id, item.quantity - 1)} aria-label="Decrease quantity">
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button type="button" onClick={() => setQuantity(item.id, item.quantity + 1)} aria-label="Increase quantity">
                        +
                      </button>
                    </div>
                    <strong>{formatPrice(item.priceCents * item.quantity, item.currency)}</strong>
                    <button type="button" className="cart-remove" onClick={() => removeItem(item.id)}>
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="cart-drawer-footer">
          <div className="cart-subtotal">
            <span className="muted">Subtotal</span>
            <strong>{formatPrice(subtotalCents, currency)}</strong>
          </div>
          <div className="cart-footer-actions">
            <Link className="btn btn-ghost" to="/marketplace" onClick={closeCart}>
              Continue Shopping
            </Link>
            <Link
              className={`btn btn-primary${items.length === 0 ? ' is-disabled' : ''}`}
              to="/checkout/details"
              onClick={(event) => {
                if (items.length === 0) {
                  event.preventDefault();
                  return;
                }
                closeCart();
              }}
            >
              Continue Purchase
            </Link>
          </div>
        </div>
      </aside>
    </div>
  );
}
